import { whatsappConfig } from "./whatsappConfig";

const SCRIPT_ID = "facebook-jssdk";
const SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

export interface FbLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

export interface FbLoginOptions {
  config_id: string;
  response_type: "code";
  override_default_response_type: boolean;
  extras: {
    setup: Record<string, unknown>;
    featureType: string;
    sessionInfoVersion: string;
  };
}

export interface FacebookSdk {
  init: (options: {
    appId: string;
    autoLogAppEvents: boolean;
    xfbml: boolean;
    version: string;
  }) => void;
  login: (
    callback: (response: FbLoginResponse) => void,
    options: FbLoginOptions
  ) => void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
  }
}

let loader: Promise<FacebookSdk> | null = null;

const initSdk = (sdk: FacebookSdk) => {
  sdk.init({
    appId: whatsappConfig.appId,
    autoLogAppEvents: true,
    xfbml: false,
    version: whatsappConfig.graphVersion,
  });
  return sdk;
};

/**
 * Loads and initialises the Facebook JS SDK exactly once.
 *
 * Deliberately not in index.html: Meta's script would then run for every user
 * on every page, including the majority who never open Paramètres >
 * Intégrations.
 */
export const loadMetaSdk = (): Promise<FacebookSdk> => {
  if (loader) return loader;

  loader = new Promise<FacebookSdk>((resolve, reject) => {
    if (window.FB) {
      resolve(initSdk(window.FB));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";

    script.onload = () => {
      if (!window.FB) {
        loader = null;
        script.remove();
        reject(new Error("Meta SDK loaded without FB global"));
        return;
      }
      resolve(initSdk(window.FB));
    };

    script.onerror = () => {
      // Drop the memo so the card's retry button can attempt a fresh load.
      loader = null;
      script.remove();
      reject(new Error("Meta SDK failed to load"));
    };

    document.body.appendChild(script);
  });

  return loader;
};
